import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { FileUploadStatus, ImageType, PointOfInterest, File } from '@/__generated__/graphql';
import { Attachment } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/types';
import {
  poiAttachmentsAtom,
  preInspectionImagesAtom,
  postInspectionImagesAtom,
  pendingRemovalIdsAtom,
} from '@/components/Analysis/modules/pointOfInterest/state/atoms';
import { getCloudfrontUrl } from '@/utils/cloudfront';

type UsePoiAttachmentsProps = {
  selectedPoiInfo: PointOfInterest;
  isUpdating?: boolean;
};

type UsePoiAttachmentsReturn = {
  poiAttachments: Attachment[];
  setPoiAttachments: (update: Attachment[] | ((prev: Attachment[]) => Attachment[])) => void;
  preInspectionImages: Attachment[];
  postInspectionImages: Attachment[];
  loading: boolean;
};

const fileToAttachment = (file: File): Attachment | null => {
  const { fileName, resourcePath, type, id, uploadStatus, caption, imageType, uuid, groupKey } =
    file;

  if (!resourcePath) {
    console.warn(`Missing resourcePath for file: ${fileName}`);
    return null;
  }

  return {
    id: id || `attachment-${fileName}-${Date.now()}`,
    name: fileName,
    type,
    isNew: false,
    caption: caption || undefined,
    url: getCloudfrontUrl(resourcePath),
    uploadStatus: uploadStatus || FileUploadStatus.Success,
    resourcePath,
    uuid: uuid || undefined,
    imageType: imageType || ImageType.Other,
    groupKey: groupKey || undefined,
  };
};

export const usePoiAttachments = ({
  selectedPoiInfo,
  isUpdating = false,
}: UsePoiAttachmentsProps): UsePoiAttachmentsReturn => {
  const poiAttachments = useAtomValue(poiAttachmentsAtom);
  const setPoiAttachments = useSetAtom(poiAttachmentsAtom);
  const preInspectionImages = useAtomValue(preInspectionImagesAtom);
  const postInspectionImages = useAtomValue(postInspectionImagesAtom);
  const pendingRemovalIds = useAtomValue(pendingRemovalIdsAtom);
  const setPendingRemovalIds = useSetAtom(pendingRemovalIdsAtom);

  useEffect(() => {
    if (!selectedPoiInfo.files) return;

    const validFiles = selectedPoiInfo.files.filter(
      (file) =>
        file.uploadStatus &&
        [FileUploadStatus.Success, FileUploadStatus.Inprogress].includes(file.uploadStatus)
    );

    const serverAttachments = validFiles
      .map((file) => fileToAttachment(file))
      .filter(Boolean) as Attachment[];

    setPoiAttachments((prev) => {
      const localPending = prev.filter((a) => a.isNew);
      if (localPending.length === 0) return serverAttachments;

      const pendingUuids = new Set(localPending.map((a) => a.uuid).filter(Boolean));
      return [
        ...serverAttachments.filter((a) => !a.uuid || !pendingUuids.has(a.uuid)),
        ...localPending,
      ];
    });
    setPendingRemovalIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoiInfo.id, selectedPoiInfo.files]);

  useEffect(() => {
    if (isUpdating) return;

    const serverFiles = selectedPoiInfo.files ?? [];
    const serverFileIds = new Set(
      serverFiles.map((f) => f.id).filter((id): id is string => id !== null && id !== undefined)
    );

    if (pendingRemovalIds.size > 0) {
      const toRelease = [...pendingRemovalIds].filter((id) => !serverFileIds.has(id));
      if (toRelease.length > 0) {
        setPendingRemovalIds((prev) => {
          const next = new Set(prev);
          toRelease.forEach((id) => next.delete(id));
          return next;
        });
        return;
      }
    }

    const validFiles = serverFiles.filter(
      (file) =>
        file.uploadStatus &&
        [FileUploadStatus.Success, FileUploadStatus.Inprogress].includes(file.uploadStatus) &&
        (file.id === null || file.id === undefined || !pendingRemovalIds.has(file.id))
    );

    const attachments = validFiles
      .map((file) => fileToAttachment(file))
      .filter(Boolean) as Attachment[];

    const processedFileIds = new Set(
      attachments.map((a) => a.id).filter((id): id is string => !!id)
    );

    setPoiAttachments((prev) => {
      const hasLocalPending = prev.some(
        (a) => a.isNew || a.uploadStatus === FileUploadStatus.Inprogress
      );
      if (hasLocalPending) return prev;

      const hasUnsyncedUploads = prev.some(
        (p) => p.id !== null && p.id !== undefined && !processedFileIds.has(p.id)
      );
      if (hasUnsyncedUploads) return prev;

      const confirmedAttachments = prev.filter((a) => !a.isNew);
      const isAllConfirmedAttachmentsStillOnServer = confirmedAttachments.every(
        (a) => !a.id || processedFileIds.has(a.id)
      );
      if (
        confirmedAttachments.length < attachments.length &&
        isAllConfirmedAttachmentsStillOnServer
      ) {
        return prev;
      }

      const isSame =
        prev.length === attachments.length && prev.every((p, idx) => p.id === attachments[idx].id);
      if (isSame) return prev;
      return attachments;
    });
  }, [
    selectedPoiInfo.files,
    isUpdating,
    pendingRemovalIds,
    setPoiAttachments,
    setPendingRemovalIds,
  ]);

  return {
    poiAttachments,
    setPoiAttachments,
    preInspectionImages,
    postInspectionImages,
    loading: false,
  };
};
